<template>
  <div class="w-full flex items-center justify-center" :style="{ height: height + 'px' }">
    <div v-if="hasData" class="text-center">
      <div class="relative w-80 h-80 mx-auto">
        <!-- SVG Donut Chart -->
        <svg viewBox="0 0 100 100" class="w-full h-full" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));">
          <g
            v-for="(segment, index) in segments"
            :key="`pie-segment-${index}`"
            class="cursor-pointer transition-all duration-300"
            @mouseenter="hoveredSegment = index"
            @mouseleave="hoveredSegment = null"
            :style="{
              transform: hoveredSegment === index ? 'scale(1.05)' : 'scale(1)',
              transformOrigin: '50px 50px'
            }"
          >
            <path
              :d="segment.path"
              :fill="segmentColors[index]"
              :opacity="hoveredSegment === null || hoveredSegment === index ? 1 : 0.7"
              class="transition-all duration-300"
              stroke="white"
              stroke-width="0.5"
            />
          </g>
        </svg>

        <!-- Tooltip -->
        <div
          v-if="hoveredSegment !== null"
          class="absolute top-1/2 pointer-events-none z-10"
          :class="getTooltipPosition(hoveredSegment)"
        >
          <div class="bg-secondary-800 dark:bg-secondary-700 text-white px-4 py-2 rounded-lg shadow-lg text-center whitespace-nowrap">
            <p class="text-xs font-semibold">{{ chartData[hoveredSegment].label }}</p>
            <p class="text-sm font-bold">{{ chartData[hoveredSegment].value }}</p>
            <p class="text-xs text-secondary-300">{{ getPercentage(chartData[hoveredSegment].value) }}%</p>
          </div>
        </div>

        <!-- Center Label -->
        <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div class="text-center bg-white dark:bg-secondary-800 rounded-full p-2">
            <p class="text-3xl font-bold text-secondary-900 dark:text-white">{{ totalValue }}</p>
            <p class="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">Total</p>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div class="mt-6 gap-2 max-w-full" :class="`grid grid-cols-${legendCols}`">
        <div
          v-for="(item, index) in chartData"
          :key="`legend-${index}`"
          class="flex flex-col items-center justify-center p-2 rounded-lg bg-secondary-50 dark:bg-secondary-700/30 hover:bg-secondary-100 dark:hover:bg-secondary-700/50 transition-colors cursor-pointer min-w-0"
          @mouseenter="hoveredSegment = index"
          @mouseleave="hoveredSegment = null"
        >
          <div class="flex items-center space-x-1 mb-1 w-full justify-center">
            <div
              class="w-2.5 h-2.5 rounded-full flex-shrink-0"
              :style="{ backgroundColor: segmentColors[index] }"
            ></div>
            <p class="text-xs font-medium text-secondary-900 dark:text-white truncate">{{ item.label }}</p>
          </div>
          <div class="flex items-center space-x-1.5">
            <p class="text-base font-bold text-secondary-900 dark:text-white">{{ item.value }}</p>
            <p class="text-xs font-medium text-secondary-500 dark:text-secondary-400 bg-secondary-200 dark:bg-secondary-600 px-1.5 py-0.5 rounded">
              {{ getPercentage(item.value) }}%
            </p>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="flex items-center justify-center h-full text-secondary-500 dark:text-secondary-400">
      <p class="text-sm">No data available</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const hoveredSegment = ref(null);

const props = defineProps({
  data: {
    type: [Array, Object],
    default: () => [],
  },
  height: {
    type: Number,
    default: 300,
  },
  colors: {
    type: Array,
    default: () => ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
  },
  legendCols: {
    type: Number,
    default: 3,
  },
});

// Handle both array format and object format
const chartData = computed(() => {
  if (Array.isArray(props.data)) {
    return props.data;
  }
  if (props.data?.labels && props.data?.datasets) {
    // Convert Chart.js-style data
    return props.data.labels.map((label, index) => ({
      label,
      value: props.data.datasets[0]?.data[index] || 0
    }));
  }
  return [];
});

const hasData = computed(() => chartData.value.length > 0 && totalValue.value > 0);

const totalValue = computed(() => {
  return chartData.value.reduce((sum, item) => sum + (item.value || 0), 0);
});

// Get colors from dataset backgroundColor if available, otherwise use default colors prop
const segmentColors = computed(() => {
  if (props.data?.datasets && props.data.datasets[0]?.backgroundColor) {
    return props.data.datasets[0].backgroundColor;
  }
  return props.colors;
});

// Calculate donut segments with SVG path (wheel style with empty center)
const segments = computed(() => {
  const centerX = 50;
  const centerY = 50;
  const outerRadius = 40;
  const innerRadius = 25; // Create the donut hole
  let currentAngle = 0;

  const result = chartData.value.map((item, index) => {
    const percentage = totalValue.value > 0 ? (item.value / totalValue.value) : 0;
    const angle = percentage * 360;

    // Convert angles to radians
    const startAngle = (currentAngle - 90) * (Math.PI / 180);
    const endAngle = (currentAngle + angle - 90) * (Math.PI / 180);

    // Calculate outer arc points
    const outerX1 = centerX + outerRadius * Math.cos(startAngle);
    const outerY1 = centerY + outerRadius * Math.sin(startAngle);
    const outerX2 = centerX + outerRadius * Math.cos(endAngle);
    const outerY2 = centerY + outerRadius * Math.sin(endAngle);

    // Calculate inner arc points
    const innerX1 = centerX + innerRadius * Math.cos(startAngle);
    const innerY1 = centerY + innerRadius * Math.sin(startAngle);
    const innerX2 = centerX + innerRadius * Math.cos(endAngle);
    const innerY2 = centerY + innerRadius * Math.sin(endAngle);

    // Determine if arc should be large (> 180 degrees)
    const largeArcFlag = angle > 180 ? 1 : 0;

    // Create SVG path for donut segment
    const path = [
      `M ${outerX1} ${outerY1}`,  // Move to outer arc start
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerX2} ${outerY2}`, // Outer arc
      `L ${innerX2} ${innerY2}`,  // Line to inner arc
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerX1} ${innerY1}`, // Inner arc (reverse)
      'Z'                          // Close path
    ].join(' ');

    currentAngle += angle;

    return {
      path,
      percentage,
      angle
    };
  });

  return result;
});

const getPercentage = (value) => {
  return totalValue.value > 0 ? ((value / totalValue.value) * 100).toFixed(1) : '0.0';
};

const getTooltipPosition = (index) => {
  if (segments.value.length === 0 || index >= segments.value.length) {
    return 'left-full ml-4 -translate-y-1/2';
  }

  // Calculate the middle angle of the segment
  let currentAngle = 0;
  for (let i = 0; i < index; i++) {
    currentAngle += segments.value[i].angle;
  }
  const midAngle = currentAngle + (segments.value[index].angle / 2);

  // Determine position based on angle (0° is top, 90° is right, 180° is bottom, 270° is left)
  // Show on right side if angle is between 315° to 45° or 45° to 135°
  // Show on left side if angle is between 135° to 315°
  if (midAngle >= 315 || midAngle < 135) {
    return 'left-full ml-4 -translate-y-1/2'; // Right side
  } else {
    return 'right-full mr-4 -translate-y-1/2'; // Left side
  }
};
</script>
